/**
 * Formatting helpers shared by the test-suite (group) screens.
 *
 * Tag tokens are the same ones the Automation Scripts grid uses, so the display
 * names and the `.automation-step-tag--*` class suffix are derived identically
 * here — no second source of truth for badge styling.
 */

export const TAG_DISPLAY_NAMES = {
  'Web-UI': 'WEB-UI',
  'Mobile': 'MOBILE-UI',
  'Api': 'API',
  'Database': 'DATABASE',
};

export const tagLabel = (tag) => TAG_DISPLAY_NAMES[tag] || String(tag).toUpperCase();

export const tagClass = (tag) => `automation-step-tag automation-step-tag--${String(tag).toLowerCase().replace('-', '')}`;

/** "04-05-2024, 12:04 PM" — the timestamp format the suite cards use. */
export const formatSuiteTimestamp = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const pad = (n) => String(n).padStart(2, '0');
  const hours24 = date.getHours();
  const meridiem = hours24 >= 12 ? 'PM' : 'AM';
  const hours12 = hours24 % 12 || 12;
  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}, ${hours12}:${pad(date.getMinutes())} ${meridiem}`;
};

/** "03 Sept 2026 09:53" — the compact stamp on run-history rows. */
export const formatRunTimestamp = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  const month = date.toLocaleDateString(undefined, { month: 'short' });
  return `${pad(date.getDate())} ${month} ${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/** "Run - 03 September 2026" — the run summary heading. */
export const formatRunHeading = (iso) => {
  if (!iso) return 'Run';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Run';
  return `Run - ${date.toLocaleDateString(undefined, { day: '2-digit', month: 'long', year: 'numeric' })}`;
};

/** Durations read as "9.1s" under a minute and "1m 3.24s" above it. */
export const formatDuration = (ms) => {
  if (!ms && ms !== 0) return '—';
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const mins = Math.floor(seconds / 60);
  return `${mins}m ${(seconds - mins * 60).toFixed(2)}s`;
};

/** "00:09" — the per-script elapsed column. */
export const formatClock = (ms) => {
  const total = Math.max(0, Math.round((ms || 0) / 1000));
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(Math.floor(total / 60))}:${pad(total % 60)}`;
};

export const passRate = (passed, total) => (total > 0 ? Math.round((passed / total) * 100) : 0);

/**
 * A time in the viewer's own system zone, with that zone's label — never UTC
 * unless the viewer is in UTC. The payloads carry UTC instants; this converts
 * them. Never render a bare time (BR-42): "09:53:29" alone cannot be read.
 *
 * @param {string} iso  UTC instant from the payload
 */
export const formatLocalTime = (iso) => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';

  const time = date.toLocaleTimeString('en-GB', { hour12: false });
  // The platform's abbreviation for the local zone (SAST, BST, GMT+2…)
  const label = new Intl.DateTimeFormat('en-GB', { timeZoneName: 'short' })
    .formatToParts(date)
    .find((part) => part.type === 'timeZoneName')?.value;
  return label ? `${time} ${label}` : time;
};

/** "00:03:28" — hours, minutes, seconds, for the report's duration tile. */
export const formatHms = (ms) => {
  if (ms == null || Number.isNaN(Number(ms))) return '—';
  const total = Math.max(0, Math.round(ms / 1000));
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(Math.floor(total / 3600))}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
};

/** Wall-clock time since an instant, as "1m 42s". */
export const formatElapsed = (startedAt, endedAt = null) => {
  if (!startedAt) return '—';
  const start = new Date(startedAt).getTime();
  if (Number.isNaN(start)) return '—';
  const end = endedAt ? new Date(endedAt).getTime() : Date.now();
  const seconds = Math.max(0, Math.round((end - start) / 1000));
  const mins = Math.floor(seconds / 60);
  return mins > 0 ? `${mins}m ${seconds % 60}s` : `${seconds}s`;
};
